/**
 * Landing — SEO-friendly public homepage for unauthenticated visitors.
 *
 * Renders at `/` when user is NOT signed in. Signed-in users are
 * redirected to the authenticated dashboard by the router wrapper.
 *
 * Content is deliberately verbose and crawler-friendly:
 *   • One <h1>
 *   • Semantic <h2>/<h3> section headings
 *   • 350+ words of substantive product copy
 *   • Internal links to /login and /trust
 *   • No hidden text, no keyword stuffing
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, ShieldCheck, LineChart, Layers,
  GitBranch, Gauge, Lock, ArrowRight,
} from 'lucide-react';
import SafetyStatusPill from '../components/landing/SafetyStatusPill';

const C_BG = '#0a0a0a';
const C_INK = '#e5e7eb';
const C_MUTED = '#71717a';
const C_TEAL = '#14b8a6';
const C_AMBER = '#f59e0b';
const C_BORDER = '#2a2f37';
const MONO = 'JetBrains Mono, ui-monospace, monospace';
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

const FEATURES = [
  {
    icon: Layers,
    title: 'Multi-Venue Smart Order Routing',
    body: 'A single execution surface that quotes across paper venues, measures spread and toxic-fill on every fill, and records mid-price references for retroactive slippage attribution.',
  },
  {
    icon: GitBranch,
    title: 'Bayesian Promotion Gate',
    body: 'Strategies must string together 14 gap-tolerant profitable days before the promotion gate opens. No opinion-based go-lives; every activation is data-earned.',
  },
  {
    icon: LineChart,
    title: 'Shadow Strategy Tournament',
    body: 'Expectancy rankings, edge-decay boards, allocator comparators, and regime compatibility matrices — all shadow-mode. Winners are visible before they touch capital.',
  },
  {
    icon: Gauge,
    title: 'Execution Telemetry Coverage',
    body: 'Every fill row carries a capture_status tag. Coverage percent is surfaced in real time so operators know how much of the day is fully instrumented.',
  },
  {
    icon: ShieldCheck,
    title: 'Hash-Chained Audit Log',
    body: 'Safety-critical flag mutations, admin actions, and governance events are appended to a tamper-evident audit chain. Every write is signed with a rolling hash.',
  },
  {
    icon: Lock,
    title: 'Explicit Live-Money Arming',
    body: 'LIVE_MODE_ENABLED stays false. To go live requires an admin JWT, cutover stage seven, a literal confirmation phrase, and a CRITICAL severity audit entry.',
  },
];

function Section({ children, style }) {
  return (
    <section style={{
      maxWidth: 1120, margin: '0 auto',
      padding: '96px 24px', ...style,
    }}>
      {children}
    </section>
  );
}

function Feature({ Icon, title, body }) {
  return (
    <article
      data-testid={`landing-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      style={{
        padding: 24,
        background: '#111318',
        border: `1px solid ${C_BORDER}`,
        borderRadius: 8,
        borderLeft: `3px solid ${C_TEAL}`,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                    marginBottom: 10 }}>
        <Icon size={18} color={C_TEAL} />
        <h3 style={{
          margin: 0, fontFamily: SANS, color: C_INK,
          fontSize: 17, fontWeight: 600,
        }}>{title}</h3>
      </div>
      <p style={{
        margin: 0, color: C_MUTED, fontSize: 14,
        lineHeight: 1.6,
      }}>{body}</p>
    </article>
  );
}

export default function Landing() {
  return (
    <main
      data-testid="landing-root"
      style={{
        background: C_BG, color: C_INK, minHeight: '100vh',
        fontFamily: SANS,
      }}>
      {/* Hero */}
      <Section>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '4px 12px', borderRadius: 999,
          background: 'rgba(20, 184, 166, 0.1)',
          border: `1px solid ${C_TEAL}`,
          color: C_TEAL, fontSize: 11, letterSpacing: 1.5,
          textTransform: 'uppercase', fontWeight: 600,
          fontFamily: MONO, marginBottom: 24,
        }}>
          <Activity size={12} /> Paper-First · Shadow Mode · Read-Only
        </div>
        <h1
          data-testid="landing-h1"
          style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1, letterSpacing: '-0.02em',
            fontWeight: 700, margin: 0, maxWidth: 900,
          }}>
          Adaptive Institutional
          <br />
          <span style={{ color: C_TEAL }}>Profitability Engine</span>
        </h1>
        <p style={{
          fontSize: 20, color: C_MUTED, marginTop: 20,
          maxWidth: 680, lineHeight: 1.6,
        }}>
          ApexFlow Engine runs every strategy in strict shadow-paper mode,
          measures edge with Bayesian confidence gates, and locks
          safety-critical flags behind a hash-chained audit trail — so
          quantitative operators can prove profitability before touching
          real capital.
        </p>
        <div style={{
          marginTop: 36, display: 'flex', gap: 16, flexWrap: 'wrap',
        }}>
          <Link
            to="/login"
            data-testid="landing-cta-login"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 24px', background: C_TEAL, color: '#0a0a0a',
              borderRadius: 6, textDecoration: 'none', fontWeight: 600,
              fontFamily: MONO, letterSpacing: 0.5, fontSize: 13,
              textTransform: 'uppercase',
            }}>
            Sign in <ArrowRight size={14} />
          </Link>
          <Link
            to="/trust"
            data-testid="landing-cta-trust"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 24px', background: 'transparent',
              color: C_INK, borderRadius: 6, textDecoration: 'none',
              fontWeight: 600, border: `1px solid ${C_BORDER}`,
              fontFamily: MONO, letterSpacing: 0.5, fontSize: 13,
              textTransform: 'uppercase',
            }}>
            Trust & compliance
          </Link>
        </div>
        <SafetyStatusPill />
      </Section>

      {/* Value narrative */}
      <Section style={{ paddingTop: 0, paddingBottom: 48 }}>
        <div style={{
          padding: '48px 40px',
          background: 'linear-gradient(180deg, #0f1115, #0a0a0a)',
          border: `1px solid ${C_BORDER}`, borderRadius: 12,
        }}>
          <h2 style={{
            fontSize: 24, letterSpacing: '-0.01em',
            fontWeight: 600, margin: 0, marginBottom: 16,
          }}>
            Why paper-first is not optional
          </h2>
          <p style={{
            color: C_MUTED, fontSize: 15, lineHeight: 1.75,
            maxWidth: 780, margin: 0,
          }}>
            Most trading platforms treat the paper-live boundary as a
            single button. ApexFlow Engine treats it as a governance
            program. Every strategy accrues shadow performance rows in a
            dedicated ledger. A gap-tolerant Bayesian streak counter
            requires fourteen consecutive profitable days before a
            promotion is even <em>proposed</em>. The promotion itself
            demands admin JWT plus cutover stage seven, a literal phrase,
            and an explicit CRITICAL audit entry — none of which can be
            forged from the outside because every mutation is
            hash-chained to the previous entry.
          </p>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <h2 style={{
          fontSize: 32, letterSpacing: '-0.02em',
          fontWeight: 700, margin: 0, marginBottom: 8,
        }}>
          What the engine measures
        </h2>
        <p style={{ color: C_MUTED, fontSize: 15, marginBottom: 40,
                    maxWidth: 600 }}>
          Every capability below runs today in shadow mode. None require
          live-money authorisation to observe.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 16,
        }}>
          {FEATURES.map(f => (
            <Feature key={f.title} Icon={f.icon}
                     title={f.title} body={f.body} />
          ))}
        </div>
      </Section>

      {/* Safety posture disclosure */}
      <Section style={{ paddingTop: 48 }}>
        <div style={{
          padding: 32, borderRadius: 10, background: '#111318',
          border: `1px solid ${C_AMBER}`, borderLeft: `4px solid ${C_AMBER}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                        marginBottom: 12 }}>
            <ShieldCheck size={16} color={C_AMBER} />
            <span style={{
              fontFamily: MONO, fontSize: 11, color: C_AMBER,
              letterSpacing: 1.5, textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              Current Safety Posture
            </span>
          </div>
          <h3 style={{ margin: 0, marginBottom: 10, fontSize: 20 }}>
            Live trading is disabled by default and cannot be enabled
            without a governance-arming procedure.
          </h3>
          <p style={{ color: C_MUTED, fontSize: 14, lineHeight: 1.7,
                      margin: 0, maxWidth: 780 }}>
            <code style={{ color: C_INK, fontFamily: MONO }}>
              LIVE_MODE_ENABLED
            </code> is <strong>false</strong>. <code style={{
              color: C_INK, fontFamily: MONO }}>EFFECTIVE_MODE</code> is{' '}
            <strong>PAPER</strong>. <code style={{
              color: C_INK, fontFamily: MONO
            }}>signal_quality_layer_enforced</code> is <strong>false</strong>.
            All operator activity is read-only or shadow-mode. No real
            venue writes occur. This posture is verifiable via the
            authenticated {' '}
            <Link to="/trust" style={{ color: C_TEAL }}>trust page</Link>.
          </p>
        </div>
      </Section>

      {/* Footer */}
      <footer
        data-testid="landing-footer"
        style={{
          borderTop: `1px solid ${C_BORDER}`, padding: '32px 24px',
          textAlign: 'center', color: C_MUTED, fontSize: 12,
          fontFamily: MONO, letterSpacing: 0.5,
        }}>
        ApexFlow Engine · Shadow-paper trading platform ·{' '}
        <Link to="/login" style={{ color: C_TEAL, textDecoration: 'none' }}>
          Sign in
        </Link>{' '}·{' '}
        <Link to="/trust" style={{ color: C_TEAL, textDecoration: 'none' }}>
          Trust & compliance
        </Link>
      </footer>
    </main>
  );
}
